import { Injectable, OnDestroy } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import * as signalR from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { environment } from 'src/assets/env/environment';

export type AuditEventType = 'Created' | 'Updated' | 'Deleted' | 'Restored';

export interface AuditEvent {
  type: AuditEventType;
  auditId: string;
  organisationId: string;
  timestampUtc: string;
}

/**
 * AuditRealtimeService
 *
 * Maintains a SignalR connection to the API's AuditHub.
 * Emits typed AuditEvents on every audit change (create / update / delete / restore).
 *
 * Consumers should NOT subscribe to this directly — use the NgRx effect
 * in AuditEffects, which debounces the signal and dispatches refreshAudits.
 */
@Injectable({
  providedIn: 'root',
})
export class AuditRealtimeService implements OnDestroy {
  private readonly auditChanged$ = new Subject<AuditEvent>();

  /** Observable of typed audit change events from the SignalR hub. */
  onAuditChanged(): Observable<AuditEvent> {
    return this.auditChanged$.asObservable();
  }

  private connection: signalR.HubConnection | null = null;
  private stopped = false;

  constructor(private auth: AuthService) {}

  /** Start the hub connection. Call once after the user is authenticated. */
  async start(): Promise<void> {
    if (this.connection) return; // already started

    this.stopped = false;

    const hubUrl = `${environment.API_BASE_URL}/hubs/audit`;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        // WebSocket cannot send headers — the token is passed as a query param.
        // SignalR's accessTokenFactory is called on every (re-)connect.
        accessTokenFactory: () =>
          this.auth.getAccessTokenSilently().toPromise() as Promise<string>,
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: false,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    this.connection.on('AuditChanged', (event: AuditEvent) => {
      this.auditChanged$.next(event);
    });

    this.connection.onreconnecting(() => {
      console.debug('[AuditRealtime] reconnecting…');
    });

    this.connection.onreconnected(() => {
      console.debug('[AuditRealtime] reconnected.');
    });

    this.connection.onclose((error) => {
      if (!this.stopped) {
        console.warn('[AuditRealtime] connection closed unexpectedly.', error);
      }
    });

    try {
      await this.connection.start();
      console.debug('[AuditRealtime] connected.');
    } catch (err) {
      console.error('[AuditRealtime] failed to connect:', err);
    }
  }

  /** Stop and dispose the hub connection. Call on logout. */
  async stop(): Promise<void> {
    this.stopped = true;
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
